import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AddContractDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddContractDialog = ({ open, onOpenChange, onSuccess }: AddContractDialogProps) => {
  const [employeeName, setEmployeeName] = useState("");
  const [terms, setTerms] = useState({
    salary_deductions: true,
    inventory_discrepancies: true,
    counting_differences: true,
    termination: true
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Vous devez être connecté pour créer un contrat");
        return;
      }

      const { error } = await supabase
        .from('employee_contracts')
        .insert({
          employee_name: employeeName,
          terms,
          owner_id: user.id
        });

      if (error) throw error;

      toast.success("Contrat créé avec succès");
      onSuccess();
      setEmployeeName("");
    } catch (error: any) {
      console.error('Error creating contract:', error);
      toast.error("Erreur lors de la création du contrat");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nouveau contrat d'employé</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="employee_name">Nom de l'employé</Label>
            <Input
              id="employee_name"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              placeholder="Entrez le nom de l'employé"
              required
            />
          </div>

          <div className="space-y-4">
            <Label>Termes du contrat</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="salary_deductions"
                  checked={terms.salary_deductions}
                  onCheckedChange={(checked) => 
                    setTerms(prev => ({ ...prev, salary_deductions: checked as boolean }))
                  }
                />
                <Label htmlFor="salary_deductions">Retenues sur salaire</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="inventory_discrepancies"
                  checked={terms.inventory_discrepancies}
                  onCheckedChange={(checked) => 
                    setTerms(prev => ({ ...prev, inventory_discrepancies: checked as boolean }))
                  }
                />
                <Label htmlFor="inventory_discrepancies">Écarts d'inventaire</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="counting_differences"
                  checked={terms.counting_differences}
                  onCheckedChange={(checked) => 
                    setTerms(prev => ({ ...prev, counting_differences: checked as boolean }))
                  }
                />
                <Label htmlFor="counting_differences">Différences de comptage</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="termination"
                  checked={terms.termination}
                  onCheckedChange={(checked) => 
                    setTerms(prev => ({ ...prev, termination: checked as boolean }))
                  }
                />
                <Label htmlFor="termination">Conditions de résiliation</Label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit">
              Créer le contrat
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};